import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector, loadChange, resetChanges } from '../redux';
import { Change } from '../models';
import {
    isCanonicalChangePath,
    pushDashboardChangeUrl,
    restoreDashboardUrl,
} from './dashboardChangeUrl';

/**
 * Dashboard widgets: open ChangeModal in local state while syncing the address bar
 * to the canonical change URL (History API only — no React Router navigation).
 */
export function useDashboardChangeModal() {
    const dispatch = useAppDispatch();
    const { change: detailChange } = useAppSelector((state) => state.changes);
    const [modalChangeId, setModalChangeId] = useState<string | undefined>();

    const closeChangeModal = useCallback(() => {
        restoreDashboardUrl();
        setModalChangeId(undefined);
        dispatch(resetChanges(true));
    }, [dispatch]);

    const openChangeModal = useCallback((item: Change) => {
        if (!item._id)
            return;
        setModalChangeId(item._id);
        dispatch(loadChange(item._id));
        pushDashboardChangeUrl(item);
    }, [dispatch]);

    useEffect(() => {
        if (!modalChangeId)
            return undefined;

        const onPopState = () => {
            if (!isCanonicalChangePath(window.location.pathname))
                closeChangeModal();
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [modalChangeId, closeChangeModal]);

    return {
        modalChangeId,
        detailChange,
        openChangeModal,
        closeChangeModal,
    };
}
